import { Box, Button, Typography, Grid, Card, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { GroupedGeoJSON, GeoJSONDataMap, GroupedGeoJSONFiles, FilterType } from "../../../utilities/types";
import { ListOfFiles } from "../CSV/ListOfFiles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Oops } from "../../../utilities/oops";
import { Add, NotInterested } from "@mui/icons-material";
import { convertFilesIntoBuffer, GeoData } from "@rogoag/shared-ts";
import { GeoTypeSelector, analyzeGeometryTypes } from "./GeoTypeSelector";
import { PropertyMappings } from "./PropertyMappingSelector";
import GlobalGeoTypeSelector from "./GlobalGeoTypeSelector";
import GlobalPropertyMappingSelector from "./GlobalPropertyMappingSelector";
import { useState, useMemo } from "react";

interface GeoTypeSelectorGroupProps {
	geoDataMap: GeoJSONDataMap;
	setGeoDataMap: React.Dispatch<React.SetStateAction<GeoJSONDataMap>>;
	selectableGeoData: GroupedGeoJSONFiles;
	setSelectableGeoData: React.Dispatch<React.SetStateAction<GroupedGeoJSONFiles>>;
	originalShapefiles: File[];
	setOriginalShapefiles: React.Dispatch<React.SetStateAction<File[]>>;
	loading: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	onPropertyMappingsChange: (groupKey: string, mappings: PropertyMappings) => void;
	onClear?: () => void;
}

export function GeoTypeSelectorGroup(props: GeoTypeSelectorGroupProps) {
	// Calculate geometry data availability across all groups and collect all properties
	const geometryAvailability = useMemo(() => {
		const allShapeCounts: Record<string, number> = {};
		const propertyMap = new Map<string, Set<string>>();

		[...props.geoDataMap.values()].flat().forEach((geoData) => {
			geoData.geoJSON.features.forEach((feature) => {
				const type = feature.geometry.type;
				allShapeCounts[type] = (allShapeCounts[type] || 0) + 1;

				// Collect properties for global selector
				if (feature.properties) {
					Object.entries(feature.properties).forEach(([key, value]) => {
						if (value !== null && value !== undefined && String(value).trim() !== "") {
							if (!propertyMap.has(key)) {
								propertyMap.set(key, new Set<string>());
							}
							propertyMap.get(key)!.add(String(value));
						}
					});
				}
			});
		});

		const hasPolygons = Object.keys(allShapeCounts).some((type) => type === "Polygon" || type === "MultiPolygon");
		const hasPoints = Object.keys(allShapeCounts).some((type) => type === "Point");

		// Convert property map to array format
		const allAvailableProperties = Array.from(propertyMap.entries())
			.map(([name, values]) => ({
				name,
				examples: Array.from(values).slice(0, 3),
			}))
			.sort((a, b) => a.name.localeCompare(b.name));

		return { hasPolygons, hasPoints, allAvailableProperties };
	}, [props.geoDataMap]);

	// State to trigger type application to groups
	const [applyTypeToGroups, setApplyTypeToGroups] = useState<{
		type: FilterType;
		target: "polygons" | "points";
		timestamp: number;
	} | null>(null);
	const [applyPropertyMapping, setApplyPropertyMapping] = useState<{
		field: keyof PropertyMappings;
		value: string;
		timestamp: number;
	} | null>(null);

	// Direct action handlers that apply to all relevant groups
	const handleApplyToPolygons = (type: FilterType) => {
		setApplyTypeToGroups({ type, target: "polygons", timestamp: Date.now() });
	};

	const handleApplyToPoints = (type: FilterType) => {
		setApplyTypeToGroups({ type, target: "points", timestamp: Date.now() });
	};

	const handleApplyPropertyMapping = (field: keyof PropertyMappings, value: string | null) => {
		if (value) {
			setApplyPropertyMapping({ field, value, timestamp: Date.now() });
		}
	};

	return (
		<>
			<Grid item xs={12}>
				<Box display={"flex"}>
					<Button
						style={{ width: "80%", marginRight: 5 }}
						variant="contained"
						onClick={async () => {
							try {
								// show file dialog
								const fileInput = document.createElement("input");
								fileInput.type = "file";
								fileInput.accept = ".zip";
								fileInput.multiple = true;
								fileInput.onchange = (event) => {
									event.preventDefault();
									props.setLoading(true);
									// @ts-ignore
									const newFiles: File[] = Array.from(event.target.files);
									const oldFiles = [...props.originalShapefiles];
									for (const newFile of newFiles) {
										if (oldFiles.find((oldFile) => oldFile.name === newFile.name)) {
											// remove from oldFiles
											const index = oldFiles.findIndex((oldFile) => oldFile.name === newFile.name);
											oldFiles.splice(index, 1);
										}
									}

									const allFiles = [...oldFiles, ...newFiles];

									const asyncProcessing = async () => {
										const fileMap = await GeoData.extractGroupedSHPs(allFiles);

										props.setOriginalShapefiles(allFiles);

										// Process files and create GeoJSONData objects
										const geoDataEntries = await Promise.all(
											Object.keys(fileMap).map(async (fullPath) => {
												const shapefiles = fileMap[fullPath];
												if (!fullPath.endsWith(".zip")) {
													fullPath = fullPath + ".zip";
												}

												const geoJSON = await GeoData.toGeoJSON(shapefiles, "SHP", fullPath);

												if (!geoJSON.features.length) {
													return [fullPath, null] as const;
												}

												// Extract property names from features
												const propertyNames = GeoData.extractPropertyNames(geoJSON);

												// Get geometry types present in the data
												const geoTypes = GeoData.countGeoTypes(geoJSON);
												let geometryTypes = Object.keys(geoTypes);

												// Normalize MultiPolygon to Polygon for grouping
												if (geometryTypes.includes("MultiPolygon")) {
													geometryTypes[geometryTypes.indexOf("MultiPolygon")] = "Polygon";
													geometryTypes = Array.from(new Set(geometryTypes));
												}

												// Create group key
												const folderLevels = fullPath.split("/");
												const groupKey = `${folderLevels.length},${propertyNames.join(",")},${geometryTypes.join(",")}`;

												// Create GeoJSONData object
												const geoData: GroupedGeoJSON = {
													groupKey: fullPath,
													geoJSON,
													propertyNames,
													geometryTypes: geometryTypes as any[],
													originalFile: new File([new Uint8Array(await convertFilesIntoBuffer(shapefiles))], fullPath),
												};

												return [groupKey, geoData] as const;
											}),
										);

										// Create new GeoJSONDataMap
										const newGeoDataMap: GeoJSONDataMap = new Map();

										for (const [groupKey, geoData] of geoDataEntries) {
											if (!geoData) continue;

											// Populate new GeoJSONDataMap
											if (!newGeoDataMap.has(groupKey)) {
												newGeoDataMap.set(groupKey, []);
											}
											newGeoDataMap.get(groupKey)?.push(geoData);
										}

										// Set the new state
										props.setGeoDataMap(newGeoDataMap);

										props.setLoading(false);
									};

									asyncProcessing();
								};
								fileInput.click();
							} catch (error) {
								Oops.handleError(error, "SubmitJobs.CSVImportForm.uploadFilesDialog");
							}
						}}
					>
						<Add className="icon-in-button" />
						Add Shapefiles
					</Button>
					<Button
						style={{ width: "20%" }}
						variant="contained"
						onClick={async () => {
							props.setGeoDataMap(new Map());
							props.setOriginalShapefiles([]);
							props.setSelectableGeoData({});
							if (props.onClear) {
								props.onClear();
							}
						}}
					>
						<NotInterested className="icon-in-button" />
						Clear
					</Button>
					{/* UploadTypes */}
				</Box>
			</Grid>
			{!!props.originalShapefiles.length && (
				<>
					<Grid item xs={12}>
						<Accordion defaultExpanded={true}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="h5">{`Original Files (${props.originalShapefiles.length} uploaded)`}</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<ListOfFiles
									files={props.originalShapefiles}
									truncate={3}
									onClear={(file: File) => {
										const newFiles = [...props.originalShapefiles];
										const index = newFiles.findIndex((f) => f.name === file.name);
										newFiles.splice(index, 1);
										props.setOriginalShapefiles(newFiles);
									}}
								/>
							</AccordionDetails>
						</Accordion>
					</Grid>
					<Grid item xs={12}>
						<Accordion defaultExpanded={true}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="h5">Data Mapping</Typography>
							</AccordionSummary>
							<AccordionDetails>
								{props.geoDataMap.size > 0 && (
									<>
										<GlobalGeoTypeSelector
											onApplyToPolygons={handleApplyToPolygons}
											onApplyToPoints={handleApplyToPoints}
											hasPolygonData={geometryAvailability.hasPolygons}
											hasPointData={geometryAvailability.hasPoints}
										/>
										<GlobalPropertyMappingSelector
											onApplyMapping={handleApplyPropertyMapping}
											availableProperties={geometryAvailability.allAvailableProperties}
										/>
									</>
								)}
								{[...props.geoDataMap.keys()]
									.sort((a, b) => {
										// Get shape counts for each group
										const getGeometryPriority = (key: string): number => {
											const geoDataList = props.geoDataMap.get(key);
											if (!geoDataList) return 2;

											// Calculate shape counts from the actual GeoJSON data
											const shapeCounts: Record<string, number> = {};
											geoDataList.forEach((data) => {
												data.geoJSON.features.forEach((feature) => {
													const type = feature.geometry.type;
													shapeCounts[type] = (shapeCounts[type] || 0) + 1;
												});
											});

											const analysis = analyzeGeometryTypes(shapeCounts);

											switch (analysis) {
												case "polygons":
													return 0; // Polygons first
												case "points":
													return 1; // Points second
												case "mixed":
													return 2; // Mixed third
												case "none":
													return 3; // Empty last
												default:
													return 4;
											}
										};

										return getGeometryPriority(a) - getGeometryPriority(b);
									})
									.map((groupKey, index) => {
										const geoDataList = props.geoDataMap.get(groupKey);

										if (!geoDataList) return null;

										// Extract data from GeoJSONData
										const propertyNames = geoDataList[0].propertyNames;
										const paths = geoDataList.map((d) => d.groupKey);

										const folderTokens = paths[0].split("/");
										const folderOptions: string[] = [];
										const optionTooltips: string[][] = [];

										for (let folderLevel = 1; folderLevel <= folderTokens.length; folderLevel++) {
											folderOptions.push("Folder Level " + folderLevel);
											const tooltip = paths.map((path) => {
												const tokens = path.split("/");
												return tokens[folderLevel - 1];
											});
											optionTooltips.push(tooltip);
										}

										propertyNames.forEach((key) => {
											optionTooltips.push(
												[
													...new Set(
														geoDataList
															.map((data) => {
																return data.geoJSON.features
																	.map((feature) => {
																		if (!feature.properties) return "";
																		return feature.properties[key] as string;
																	})
																	.flat();
															})
															.flat(),
													),
												].filter((value) => value !== "" && value !== undefined),
											);
										});

										// Create stable options array to prevent infinite re-renders
										const stableOptions = [...folderOptions, ...propertyNames];

										return (
											<Card elevation={4} style={{ marginBottom: 10, padding: 15 }} key={groupKey}>
												<GeoTypeSelector
													index={index + 1}
													options={stableOptions}
													inputData={geoDataList}
													setOutputFiles={(geoCollections, type) => {
														// Only update if this group still exists in geoDataMap and callback is provided
														if (props.geoDataMap.has(groupKey)) {
															props.setSelectableGeoData((prevData) => {
																const newData = { ...prevData };
																if (!newData[groupKey]) {
																	newData[groupKey] = {
																		Boundary: [],
																		Zones: [],
																		Centroids: [],
																		Cores: [],
																		Unknown: [],
																	};
																}
																newData[groupKey][type] = geoCollections;
																return newData;
															});
														}
													}}
													optionTooltips={optionTooltips}
													onPropertyMappingsChange={(mappings) => props.onPropertyMappingsChange(groupKey, mappings)}
													applyTypeToGroups={applyTypeToGroups}
													applyPropertyMapping={applyPropertyMapping}
													groupKey={groupKey}
												/>
											</Card>
										);
									})}
							</AccordionDetails>
						</Accordion>
					</Grid>
				</>
			)}
		</>
	);
}
